import React from 'react';

import { client } from '~/api/client';
import {
  AgencyUserState,
  GetAgencyUsersDocument,
  type GetAgencyUsersQuery,
  type GetAgencyUsersQueryVariables,
  SearchRecipientsDocument,
  type SearchRecipientsQuery,
  type SearchRecipientsQueryVariables
} from '~/api/graphql';
import type { RecipientSuggestion } from '~/components/recipient-input';

/**
 * Both caps are about the dropdown, not the wire: a sender who has to scroll it
 * has already lost the race against typing the address out. Colleagues get the
 * smaller share because only a few are ever worth offering.
 */
const AMS_RESULT_LIMIT = 10;
const COLLEAGUE_RESULT_LIMIT = 5;

type Colleague = { name: string; email: string };

/**
 * Colleague rows carry two facts on their one secondary line: which Jane this
 * is, and why she is on offer at all when the AMS has never heard of her.
 */
export const colleagueSuggestions = (
  colleagues: Colleague[],
  query: string
): RecipientSuggestion[] => {
  const needle = query.trim().toLowerCase();
  return colleagues
    .filter(
      (colleague) =>
        !!colleague.email &&
        (needle === '' ||
          colleague.name.toLowerCase().includes(needle) ||
          colleague.email.toLowerCase().includes(needle))
    )
    .slice(0, COLLEAGUE_RESULT_LIMIT)
    .map((colleague) => ({
      email: colleague.email,
      ...(colleague.name ? { name: colleague.name } : {}),
      description: colleague.name ? `Colleague · ${colleague.email}` : 'Colleague'
    }));
};

/**
 * Colleagues lead: the agency's own name for a person beats whatever the AMS
 * recorded for the same address, and the first row is the one Enter commits.
 * Addresses are compared case-insensitively because a mailbox is.
 */
export const mergeSuggestions = (
  colleagues: RecipientSuggestion[],
  amsParties: RecipientSuggestion[]
): RecipientSuggestion[] => {
  const seen = new Set<string>();
  return [...colleagues, ...amsParties].filter((suggestion) => {
    const address = suggestion.email.trim().toLowerCase();
    if (!address || seen.has(address)) {
      return false;
    }
    seen.add(address);
    return true;
  });
};

/**
 * A failed lookup resolves empty rather than rejecting, and each source fails
 * on its own: a dead AMS integration still leaves the colleague list, and
 * either way the sender can keep typing an address the directory never knew.
 */
const searchAmsParties = (query: string): Promise<RecipientSuggestion[]> =>
  client
    .query<SearchRecipientsQuery, SearchRecipientsQueryVariables>({
      query: SearchRecipientsDocument,
      variables: { query, limit: AMS_RESULT_LIMIT }
    })
    .then((result) =>
      (result.data?.searchRecipients ?? []).map((party) => ({
        email: party.email,
        ...(party.name ? { name: party.name } : {}),
        ...(party.description ? { description: party.description } : {})
      }))
    )
    .catch(() => []);

/**
 * The composer's contact lookup: AMS contacts and accounts from the server,
 * merged with the agency's own people, in the shape `RecipientInput` renders.
 *
 * `searchRecipients` deliberately omits colleagues — they are not AMS parties —
 * so they are filtered in memory here, the way `BaseUserFilter` and the team
 * member picker already do it.
 *
 * Nothing here debounces or cancels: `RecipientInput` owns both, and doing it
 * twice would double the delay. The returned callback is stable, so the
 * field's lookup effect does not restart on every composer render.
 */
export const useRecipientSearch = (): ((query: string) => Promise<RecipientSuggestion[]>) => {
  // Colleagues change on the scale of hiring, not of keystrokes, so the list is
  // read once per composer. The in-flight promise is what gets cached, not just
  // its result: To, Cc and Bcc can all be searching the same moment.
  const colleaguesRef = React.useRef<Promise<Colleague[]> | null>(null);

  const loadColleagues = React.useCallback((): Promise<Colleague[]> => {
    if (!colleaguesRef.current) {
      colleaguesRef.current = client
        .query<GetAgencyUsersQuery, GetAgencyUsersQueryVariables>({
          query: GetAgencyUsersDocument,
          variables: {}
        })
        .then((result) =>
          (result.data?.agencyUsers ?? [])
            // Someone who has lost access to the agency is someone who left it,
            // and offering them keyboard-first is how a client email goes to an
            // ex-employee.
            .filter((user) => user.state === AgencyUserState.Active)
            .map((user) => ({ name: user.name, email: user.email }))
        )
        .catch(() => {
          // A failed read is not cached, so the next keystroke retries instead
          // of leaving the composer colleague-less for as long as it is open.
          colleaguesRef.current = null;
          return [];
        });
    }
    return colleaguesRef.current;
  }, []);

  return React.useCallback(
    async (query: string): Promise<RecipientSuggestion[]> => {
      const [colleagues, amsParties] = await Promise.all([
        loadColleagues(),
        searchAmsParties(query)
      ]);
      return mergeSuggestions(colleagueSuggestions(colleagues, query), amsParties);
    },
    [loadColleagues]
  );
};
